import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
	listReportSubscriptions,
	updateReportSubscription,
	toggleReportSubscription,
	deleteReportSubscription,
	dtoToViewModel,
} from "services/reportSubscriptions";
import { showError, showSuccess } from "../../../../redux/common/actions";
import { useCreateReportSubscription } from "./useCreateReportSubscription";

// Owns the subscription list state for the profile card. Loads on mount,
// exposes mutations that round-trip through the API and locally update the
// list to keep the UI snappy. All mutations show user feedback via the
// existing redux toast actions.
//
// Page Headers that only need to *create* a subscription should use
// useCreateReportSubscription instead — that hook doesn't trigger the list
// fetch on mount.
export const useReportSubscriptions = () => {
	const dispatch = useDispatch();
	const [subscriptions, setSubscriptions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const reload = useCallback(async () => {
		setLoading(true);
		const res = await listReportSubscriptions();
		setLoading(false);
		if (!res?.status) {
			setError(res?.data?.detail || "Could not load subscriptions");
			return;
		}
		setError(null);
		setSubscriptions((res.data || []).map(dtoToViewModel));
	}, []);

	useEffect(() => {
		reload();
	}, [reload]);

	const onCreated = useCallback(
		(vm) => setSubscriptions((list) => [vm, ...list]),
		[]
	);
	const create = useCreateReportSubscription({ onSuccess: onCreated });

	const update = useCallback(
		async (id, apiBody) => {
			const res = await updateReportSubscription(id, apiBody);
			if (!res?.status) {
				dispatch(showError(res?.data?.error || "Could not update subscription"));
				return null;
			}
			const vm = dtoToViewModel(res.data);
			setSubscriptions((list) => list.map((s) => (s.id === id ? vm : s)));
			dispatch(showSuccess("Subscription updated."));
			return vm;
		},
		[dispatch]
	);

	const toggle = useCallback(
		async (id) => {
			const current = subscriptions.find((s) => s.id === id);
			if (!current) return;
			// Optimistic flip; revert on failure.
			const next = !current.isActive;
			setSubscriptions((list) =>
				list.map((s) => (s.id === id ? { ...s, isActive: next } : s))
			);
			const res = await toggleReportSubscription(id, next);
			if (!res?.status) {
				setSubscriptions((list) =>
					list.map((s) => (s.id === id ? { ...s, isActive: !next } : s))
				);
				dispatch(showError(res?.data?.error || "Could not change subscription state"));
			}
		},
		[subscriptions, dispatch]
	);

	const remove = useCallback(
		async (id) => {
			const res = await deleteReportSubscription(id);
			if (!res?.status) {
				dispatch(showError(res?.data?.error || "Could not delete subscription"));
				return false;
			}
			setSubscriptions((list) => list.filter((s) => s.id !== id));
			dispatch(showSuccess("Subscription deleted."));
			return true;
		},
		[dispatch]
	);

	return {
		subscriptions,
		loading,
		error,
		reload,
		create,
		update,
		toggle,
		remove,
	};
};
